/**
 * ECommerce flow data resume panel
 *
 * @author Fabio Dal Santo <fabio@gestionaleauto.com>
 */

/**
 * Main
 */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
/**
 * Material UI
 */
import { makeStyles } from '@mui/styles';
/**
 * Redux
 */
import { useDispatch, useSelector } from 'react-redux';
import { leadState } from '../../redux/leadSlice';
import { setStep, setStepValidation } from '../../redux/wizardSlice';
import { lookupsState } from '../../redux/lookupsSlice';
/**
 * Helpers
 */
import formatters from '../../helpers/formatters';
/**
 * Components
 */
import SlimButton from '../../components/SlimButton';
import File from '../../components/File';

const useStyles = makeStyles((theme) => ({
  box: {
    padding: 20,
    borderRadius: 16,
    marginTop: 20,
    backgroundColor: theme.palette.secondary.main,
    [theme.breakpoints.up('md')]: {
      marginLeft: 10,
    },
  },
  section: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 20px',
    borderRadius: 9,
  },
  property: {
    textAlign: 'left',
    marginBottom: 6,
  },
  sectionData: {
    margin: '17px 15px 30px 15px',
  },
  title: {
    fontSize: 16,
    color: theme.palette.primary.main,
  },
  vehiclePrice: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
}));

/**
 * @returns {JSX.Element}
 */
function ResumeContent() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { configuration } = useSelector(lookupsState);
  const { iban, bic } = configuration.financial;
  const dispatch = useDispatch();
  const {
    personalData, files, quote,
    financialData, cookie,
  } = useSelector(leadState);
  const modifySection = (step) => {
    dispatch(setStep(step));
  };
  useEffect(() => {
    dispatch(setStepValidation(true));
  }, []);
  /**
   * Auxiliar renders
   */
  const property = (title, value) => (
    <div className={`${classes.property} ga-ecommerce-content-data-property`}>
      <strong className="title">{title}</strong>
      <span className="value">{value ? `: ${value}` : ''}</span>
    </div>
  );
  const sectionTitle = (title, linkToStep) => (
    <div className={`${classes.section} ga-ecommerce-sidebar-top ga-ecommerce-content-title`}>
      <div className={`${classes.title} title`}>
        <strong>{title}</strong>
      </div>
      <SlimButton title={t('Modify')} onClick={() => modifySection(linkToStep)} />
    </div>
  );
  const transferResume = () => (
    <>
      <div className="col-xs-12 col-md-12 mb-3" style={{ textAlign: 'left' }}>
        <span className={`${classes.property}`}>{t('Payment by bank transfer')}</span>
      </div>
      <div className="col-xs-12 col-md-12">
        <div className="row">
          <div className="col-xs-12 col-md-7">
            <div className={`${classes.property}`}>{iban ? `IBAN: ${iban}` : ''}</div>
          </div>
          <div className="col-xs-12 col-md-5">
            <div className={`${classes.property}`}>{bic ? `BIC: ${bic}` : ''}</div>
          </div>
        </div>
      </div>
    </>
  );
  const total = () => financialData.quoteValue * financialData.fee + financialData.advance;
  const financialResume = () => (
    <>
      <div className="col-xs-12 col-md-12 mb-3" style={{ textAlign: 'left' }}>
        <span className={classes.property}>{t('Payment through a financial institution')}</span>
      </div>
      <div className="col-xs-12 col-md-6">
        {property(t('Advance'), `${formatters.thousand(financialData.advance)} €`)}
      </div>
      <div className="col-xs-12 col-md-6">
        {property(t('Months'), financialData.fee)}
      </div>
      <div className="col-xs-12 col-md-6">
        {property(t('Quote value'), `${formatters.thousand(financialData.quoteValue, 0)} €`)}
      </div>
      <div className="col-xs-12 col-md-6">
        {property(t('Total'), `${formatters.thousand(total(), 0)} €`)}
      </div>
    </>
  );
  const resumeTrade = () => (
    <>
      <div
        className="ga-ecommerce-content-data-trade"
        style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}
      >
        <div className="ga-ecommerce-content-data-trade-vehicle">
          <strong className="mr-2">{quote.makeText || cookie.makeText}</strong>
          <span>{quote.versionText || cookie.versionText}</span>
        </div>
        <div className={`${classes.vehiclePrice} ga-ecommerce-content-data-trade-price`}>
          { !quote.quoteValue && !cookie.quoteValue && (
            t('n/a')
          )}
          { (quote.quoteValue || cookie.quoteValue) && (
            `${formatters.thousand(quote.quoteValue || cookie.quoteValue)} €`
          )}
        </div>
      </div>
      <div className="d-flex flex-wrap ga-ecommerce-content-data-trade-files">
        { quote.files.map((el) => <File key={el.key} file={el} readOnly />) }
      </div>
    </>
  );
  return (
    <div className="col-xs-12">
      <div className={classes.box}>
        { /* Account section */ }
        {sectionTitle(t('Account'), 0)}
        <div className={`row ${classes.sectionData} ga-ecommerce-content-data`}>
          <div className="col-xs-12 col-md-6">
            {property(t('Name'), personalData.name)}
          </div>
          <div className="col-xs-12 col-md-6">
            {property(t('Surname'), personalData.surname)}
          </div>
          <div className="col-xs-12 col-md-6">
            {property(t('E-mail'), personalData.email)}
          </div>
          <div className="col-xs-12 col-md-6">
            {property(t('Phone'), personalData.phone)}
          </div>
        </div>
        { /* Documents section */ }
        {sectionTitle(t('Uploaded documents'), 4)}
        <div className={`d-flex flex-wrap ${classes.sectionData} ga-ecommerce-content-data files`}>
          { files.map((el) => <File key={el.key} file={el} readOnly />) }
          { files.length === 0 && property(t('No documents uploaded'))}
        </div>
        { /* Payments section */ }
        {sectionTitle(t('Payment'), 3)}
        <div className={`row ${classes.sectionData} ga-ecommerce-content-data`}>
          { financialData.type === 'transfer' && transferResume() }
          { financialData.type === 'financial' && financialResume() }
        </div>
        { /* Trade section */ }
        {personalData.tradeIn && (
          <>
            {sectionTitle(t('Trade'), 1)}
            <div className={`row ${classes.sectionData} ga-ecommerce-content-data`}>
              { resumeTrade() }
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ResumeContent;
